/**
 * Set-password step of the self-service email signup.
 *
 * Reached right after the verification code logs the user in (the account was
 * created with no usable password, so allauth's change-password endpoint acts
 * as "set password" and needs no current password). On success the flow
 * continues into the "Create workspace" wizard. The password can be changed
 * later from profile settings.
 */
import type { FormEvent } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { changePassword } from '@/lib/allauth';
import { clearEmailVerificationHandoff } from '@/lib/auth/emailVerification';
import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import { FIRST_STEP_PATH } from '@/lib/self-service/constants';

// Mirror Django's MinimumLengthValidator (min_length: 12, see
// AUTH_PASSWORD_VALIDATORS in ahoy/settings.py) so the client doesn't accept a
// password the server will reject.
const MIN_PASSWORD_LENGTH = 12;

const PASSWORD_FIELDS = {
  password: {
    required: true,
    validate: (value: unknown) =>
      typeof value === 'string' && value.length >= MIN_PASSWORD_LENGTH
        ? undefined
        : `Use at least ${MIN_PASSWORD_LENGTH} characters`,
  },
} as const;

const GENERIC_PASSWORD_ERROR =
  "We couldn't save that password. Try again, and get in touch if it keeps happening.";

export function SelfServicePasswordContent() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: PASSWORD_FIELDS,
    values: { password },
  });
  const passwordField = getFieldProps('password');

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(null);
      touchAll();
      if (!isValid || isSubmitting) {
        return;
      }
      setIsSubmitting(true);
      try {
        const resp = await changePassword({ new_password: password });
        // Only a 200 is success. Do NOT treat `meta.is_authenticated` as
        // success: allauth returns 401 WITH `meta.is_authenticated` when the
        // session needs reauthentication, and the password change did NOT
        // happen. Treating that as success would clear the handoff and advance
        // to the wizard with the password unchanged; letting it fall through
        // lets the shared reauthentication redirect handle the 401.
        if (resp.status === 200) {
          clearEmailVerificationHandoff();
          navigate(FIRST_STEP_PATH);
          return;
        }
        // 400: Django's password validators explain what to change.
        setError(resp.errors?.[0]?.message ?? GENERIC_PASSWORD_ERROR);
      } catch {
        setError(GENERIC_PASSWORD_ERROR);
      } finally {
        setIsSubmitting(false);
      }
    },
    [touchAll, isValid, isSubmitting, password, navigate]
  );

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='font-display text-4xl font-medium text-brand-600 dark:text-brand-400'>
          Create a password
        </h1>
        <p className='text-base text-[var(--color-text-tertiary)]'>
          Your email's confirmed. Set a password to sign in with next time – you
          can change it any time in your profile.
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate className='flex flex-col gap-3'>
        <Input
          type='password'
          name='password'
          aria-label='Password'
          placeholder='Enter a password'
          autoComplete='new-password'
          isRequired
          autoFocus
          value={password}
          onChange={setPassword}
          onBlur={passwordField.onBlur}
          isInvalid={passwordField.showError}
          showError={passwordField.showError}
        />
        {passwordField.showError && passwordField.error && (
          <HintText isInvalid showError>
            {passwordField.error}
          </HintText>
        )}
        <Button
          type='submit'
          color='primary'
          size='md'
          className='w-full'
          isDisabled={isSubmitting}
          disabledTooltip='Saving your password…'
          isLoading={isSubmitting}
          showTextWhileLoading
        >
          {isSubmitting ? 'Saving…' : 'Continue'}
        </Button>
        {error && (
          <div role='alert'>
            <HintText isInvalid showError>
              {error}
            </HintText>
          </div>
        )}
      </form>
    </div>
  );
}
